import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { withRetry } from '../utils/firestoreRetry';
import {
  leakDemoEnabled,
  getLeakStatus as demoStatus,
  getLeakRegions as demoRegions,
  LEAK_BADGE_WINDOW_MS,
  RAPID_BADGE_WINDOW_MS,
} from '../utils/leakDemo';

// ── Leak data: saved history + the live rapid event ─────────────────────────
//
// Two sources, merged per tank:
//
//   • HISTORY — devices/{id}/leakStats. Written by the cloud when an alert
//     fires (first alert AND every follow-up) and when an event settles. Read
//     once per page visit, and again when an event settles.
//   • LIVE — the device record the dashboard already streams. It carries the
//     rapid event in progress (usageEpisode) and the last one that settled
//     (usageLastSettled), updated on every push. No extra reads.
//
// While a rapid event is in progress the LIVE copy is the truth, so the badge,
// the modal and the chart move with it instead of waiting for the next text.
// Once history has the event's settled entry, history wins.
//
// Cost-optimized: only leak-enabled devices are ever read (callers pass the
// gate). The badge reads at most 4 daily docs; the chart reads the days in the
// current range (capped). When ?leakdemo is on, the mock overrides everything.
//
// leakStats doc: { date:'YYYY-MM-DD', tank1:{severity,escalation,usageHits[]}, tank2:{...} }
// The night window is 1–5am HST: 11:00–15:00 UTC of `date` (HST = UTC-10).

const H = 3600000;
const NIGHT_START_UTC = 'T11:00:00Z'; // 1am HST
const NIGHT_END_UTC = 'T15:00:00Z';   // 5am HST

// Rapid follow-ups go out at 30 min / 2 h / 6 h, and each one is saved. A
// history-only episode (no live copy available) counts as ongoing for longer
// than the longest of those gaps, so it is never shown as finished between two
// texts. Same reasoning as away's 28 h against its ~24 h follow-ups.
const RAPID_ONGOING_MS = 7 * H;
const AWAY_ONGOING_MS = 28 * H;
// Live rapid event with no new low for this long → "confirming it has stopped".
// It only becomes "settled" when the cloud says so (30 quiet minutes).
const CONFIRM_AFTER_MS = 10 * 60 * 1000;

// Hawaii calendar date (YYYY-MM-DD) for an epoch-ms instant.
function hstDate(ms) {
  return new Date(ms - 10 * H).toISOString().slice(0, 10);
}

function docList(snap) {
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => d.date);
}

// Collect one inline-hit array (usageHits / awayHits) across all docs.
function collectHits(docs, key, field) {
  const out = [];
  for (const d of docs) {
    const arr = d[key] && Array.isArray(d[key][field]) ? d[key][field] : [];
    for (const h of arr) out.push(h);
  }
  return out;
}

// Union of crossed-rule lists, keeping the largest drop seen per rule.
function unionCrossed(a, b) {
  const out = Array.isArray(a) ? a.map((c) => ({ ...c })) : [];
  for (const c of (Array.isArray(b) ? b : [])) {
    if (!c || typeof c.gallons !== 'number' || typeof c.windowMinutes !== 'number') continue;
    const hit = out.find((x) => x.gallons === c.gallons && x.windowMinutes === c.windowMinutes);
    if (hit) hit.observedGal = Math.max(hit.observedGal || 0, c.observedGal || 0);
    else out.push({ gallons: c.gallons, windowMinutes: c.windowMinutes, observedGal: c.observedGal || 0 });
  }
  return out;
}

// Group episode hits into ONE region each (all entries share the episode's
// startT). The episode is closed only if its LATEST close entry is at or after
// every other entry — so the result never depends on the order the docs were
// read in, and an episode that carried on after a close is not shown as over.
// Otherwise it is "ongoing" (endTs null) while its latest entry is recent.
function episodeRegions(hits, ongoingMs, now = Date.now()) {
  const groups = new Map();
  for (const h of hits) {
    if (!h || !h.startT || !h.endT) continue;
    const g = groups.get(h.startT) || {
      startT: h.startT, openEnd: 0, closedEnd: null, obs: 0, gallons: null, windowMinutes: null,
      crossed: [], gapMs: 0, endAfterGap: false,
    };
    if (h.closed === true) {
      if (g.closedEnd == null || h.endT >= g.closedEnd) {
        g.closedEnd = h.endT;
        g.endAfterGap = h.endAfterGap === true;
      }
    } else if (h.endT > g.openEnd) {
      g.openEnd = h.endT;
    }
    if (typeof h.observedGal === 'number' && h.observedGal > g.obs) g.obs = h.observedGal;
    if (h.gallons != null) g.gallons = h.gallons;
    if (h.windowMinutes != null) g.windowMinutes = h.windowMinutes;
    if (typeof h.gapMs === 'number' && h.gapMs > g.gapMs) g.gapMs = h.gapMs;
    g.crossed = unionCrossed(g.crossed, h.crossed);
    groups.set(h.startT, g);
  }
  return [...groups.values()].map((g) => {
    const closed = g.closedEnd != null && g.closedEnd >= g.openEnd;
    const end = closed ? g.closedEnd : Math.max(g.openEnd, g.closedEnd || 0);
    const ongoing = !closed && (now - end < ongoingMs);
    return {
      startTs: g.startT, endTs: ongoing ? null : end, endMs: end, closed,
      observedGal: g.obs, gallons: g.gallons, windowMinutes: g.windowMinutes,
      crossed: g.crossed, gapMs: g.gapMs, endAfterGap: closed && g.endAfterGap,
    };
  });
}

// ── Live rapid event (device record) ─────────────────────────────────────────
//
// `live` = { episodes, lastSettled, offline, lastSeenMs } from the device doc.
// Older cloud versions omit some fields; everything below falls back safely.

// The in-progress rapid event for a tank, as a region, or null.
function liveRapid(live, tankNum, now) {
  const ep = live && live.episodes && live.episodes[tankNum];
  // endedAt set = parked by the previous cloud model, i.e. already settled.
  if (!ep || ep.endedAt != null || typeof ep.startT !== 'number') return null;
  const peak = typeof ep.peakGal === 'number' ? ep.peakGal : 0;
  const low = typeof ep.minGal === 'number' ? ep.minGal : peak;
  const total = Math.max(0, peak - low);
  const lastDrop = typeof ep.lastDropT === 'number' ? ep.lastDropT : ep.startT;
  const state = live.offline ? 'offline'
    : (now - lastDrop >= CONFIRM_AFTER_MS ? 'confirming' : 'dropping');
  const crossed = Array.isArray(ep.crossed) && ep.crossed.length ? ep.crossed
    : (typeof ep.gallons === 'number'
      ? [{ gallons: ep.gallons, windowMinutes: ep.windowMinutes, observedGal: total }] : []);
  return {
    startTs: ep.startT, endTs: null, endMs: now, closed: false, live: true, state,
    observedGal: total, crossed, gallons: ep.gallons ?? null, windowMinutes: ep.windowMinutes ?? null,
    lastDropTs: lastDrop, updatedTs: ep.lastSeenT || live.lastSeenMs || null,
    gapMs: ep.gapMs || 0,
  };
}

// History + live for one tank's rapid events. Live replaces the history copy of
// the same event unless history has already recorded it settling; the last
// settled event on the device record stands in until its history entry lands.
function rapidRegionsForTank(docs, key, live, tankNum, now) {
  let regs = episodeRegions(collectHits(docs, key, 'usageHits'), RAPID_ONGOING_MS, now);
  const ls = live && live.lastSettled && live.lastSettled[tankNum];
  if (ls && typeof ls.startT === 'number' && typeof ls.endT === 'number') {
    const settled = {
      startTs: ls.startT, endTs: ls.endT, endMs: ls.endT, closed: true,
      observedGal: ls.totalGal || 0, crossed: Array.isArray(ls.crossed) ? ls.crossed : [],
      gapMs: ls.gapMs || 0, endAfterGap: ls.endAfterGap === true,
    };
    const h = regs.find((r) => r.startTs === ls.startT);
    if (!h) regs.push(settled);
    else if (!h.closed) {
      regs = regs.map((r) => (r === h ? {
        ...h, ...settled, gallons: h.gallons, windowMinutes: h.windowMinutes,
        observedGal: Math.max(h.observedGal || 0, settled.observedGal),
        crossed: unionCrossed(h.crossed, settled.crossed),
      } : r));
    }
  }
  const lv = liveRapid(live, tankNum, now);
  if (lv) {
    const same = regs.find((r) => r.startTs === lv.startTs);
    if (!(same && same.closed)) {
      regs = regs.filter((r) => r.startTs !== lv.startTs);
      regs.push({ ...lv, crossed: unionCrossed(same && same.crossed, lv.crossed) });
    }
  }
  return regs.map((r) => ({
    ...r,
    state: r.state || (r.closed ? 'settled' : (r.endTs == null ? 'dropping' : 'ended')),
  }));
}

// ── Per-viewer filtering ─────────────────────────────────────────────────────
// leakStats is DEVICE-wide (one set of hits, shared by everyone who claims the
// device), but every family is opt-IN per user and rapid rules are per user. So
// the badge and the Trends bands are filtered to the VIEWER'S OWN settings:
//   • slow (nightly)  → only if they turned "Watch for slow leaks" on
//   • away            → only if they turned "Away mode" on
//   • rapid/usage     → only an event that crossed one of their own enabled rules
// so nobody ever sees a badge/band for a threshold or family they didn't set.
// Slow-leak watch, optionally scoped to one tank. `alerts.leak.tank` of 1 or 2
// limits it to that tank; anything else means both — same "unset = both"
// convention as the rapid rules' r.tank below, and it mirrors the cloud's
// leakAppliesToTank so a badge never disagrees with what actually gets sent.
const slowOn = (alerts, tankNum) => {
  const cfg = alerts && alerts.leak;
  if (!cfg || cfg.on !== true) return false;
  if (cfg.tank !== 1 && cfg.tank !== 2) return true;
  return cfg.tank === tankNum;
};
const awayOn = (alerts) => !!(alerts && alerts.away && alerts.away.on === true);

// The viewer's enabled rapid rules that apply to this tank (tank 1/2, or both).
function viewerRapidRules(alerts, tankNum) {
  if (!alerts || alerts.usageOn !== true || !Array.isArray(alerts.usage)) return [];
  return alerts.usage.filter((r) => r && r.on === true
    && typeof r.gallons === 'number' && typeof r.windowMinutes === 'number'
    && (r.tank === tankNum || (r.tank !== 1 && r.tank !== 2)));
}

// Which of the viewer's rules a rapid event crossed. Returns the matched rule
// (narrowest window, then largest drop) so the badge/modal shows the VIEWER'S
// threshold, or null when none of their rules was crossed.
//
// Events saved by the current cloud carry the list of rules they crossed, and
// the match is the exact one the texts use (same gallons AND same window) —
// so the app and the texts can't disagree about whose alert this is. Older
// entries without that list fall back to comparing the measured drop.
function matchRapidRule(rules, region) {
  const crossed = region && Array.isArray(region.crossed) ? region.crossed : [];
  if (crossed.length) {
    const hits = [];
    for (const r of rules) {
      const c = crossed.find((x) => x.gallons === r.gallons && x.windowMinutes === r.windowMinutes);
      if (c) hits.push({ r, obs: c.observedGal || 0 });
    }
    if (!hits.length) return null;
    hits.sort((a, b) => (a.r.windowMinutes - b.r.windowMinutes) || (b.obs - a.obs));
    return hits[0].r;
  }
  const obs = (region && region.observedGal) || 0;
  const wm = region ? region.windowMinutes : null;
  const hits = rules.filter((r) => r.gallons <= obs && (wm == null || r.windowMinutes >= wm));
  if (!hits.length) return null;
  hits.sort((a, b) => (a.windowMinutes - b.windowMinutes) || (b.gallons - a.gallons));
  return hits[0];
}

// Per-user "dismissed-through" timestamp for a badge kind on a tank. A SETTLED
// episode whose end is at/before this is hidden from the badge (the user has seen
// and cleared it) — while Trends bands and the sent alert are untouched, and a
// NEW drop after this still shows. 0 = never dismissed.
function dismissedThrough(alerts, kind, tankNum) {
  const v = alerts && alerts.leakDismiss && alerts.leakDismiss[kind] && alerts.leakDismiss[kind][tankNum];
  return typeof v === 'number' ? v : 0;
}

// Stable dependency key for the slice of `alerts` that affects filtering, so the
// effects don't re-run on unrelated claim-doc changes.
function alertsFilterKey(alerts) {
  const a = alerts || {};
  return JSON.stringify({
    // include the tank scope so the badge recomputes when it changes
    leak: a.leak && a.leak.on === true ? (a.leak.tank ?? 0) : false,
    away: a.away && a.away.on === true,
    usageOn: a.usageOn === true,
    usage: Array.isArray(a.usage)
      ? a.usage.filter((r) => r && r.on === true).map((r) => [r.tank ?? 0, r.gallons, r.windowMinutes])
      : null,
    dismiss: a.leakDismiss || null, // recompute the badge when a dismissal is written
  });
}

// Per-tank badge status: the most recent of a confirmed slow leak (48h), an
// away-mode leak (48h), or a rapid/heavy-usage event. A rapid event shows for
// as long as it is in progress, then for 24h after it settles. Rapid carries
// the live state and figures for the modal; slow/away are the same badge
// ("Leak detected").
export function buildStatus(docs, alerts, live = null, now = Date.now()) {
  const out = {};
  for (const key of ['tank1', 'tank2']) {
    const tankNum = key === 'tank1' ? 1 : 2;
    let slow = null;   // slow OR away — whichever is most recent (same badge)
    let rapid = null;
    const rapidRules = viewerRapidRules(alerts, tankNum);

    // Slow: confirmed nightly leak (48h window). Carries the measured overnight
    // drop (gallons) for the modal. Only if the viewer watches for slow leaks.
    const dismSlow = dismissedThrough(alerts, 'slow', tankNum);
    const dismAway = dismissedThrough(alerts, 'away', tankNum);
    const dismRapid = dismissedThrough(alerts, 'rapid', tankNum);

    if (slowOn(alerts, tankNum)) for (const d of docs) {
      const t = d[key];
      if (!t || t.escalation !== 'confirmed') continue;
      const endTs = Date.parse(d.date + NIGHT_END_UTC);
      if (endTs <= dismSlow) continue; // dismissed (nightly leaks are always settled)
      if (now - endTs <= LEAK_BADGE_WINDOW_MS && (!slow || endTs > slow._ts)) {
        slow = {
          active: true, kind: 'slow',
          startTs: (t.night && t.night.startT) || Date.parse(d.date + NIGHT_START_UTC),
          endTs: (t.night && t.night.endT) || null,
          observedGal: (t.night && t.night.dropGal) || null,
          _ts: endTs,
        };
      }
    }
    // Away: same badge as slow, within 48h — end from the (closed) region, and
    // the gallons lost so far for the modal. Only if the viewer runs Away mode.
    if (awayOn(alerts)) for (const r of episodeRegions(collectHits(docs, key, 'awayHits'), AWAY_ONGOING_MS, now)) {
      if (now - r.endMs > LEAK_BADGE_WINDOW_MS) continue;
      if (r.endTs != null && r.endMs <= dismAway) continue; // settled + dismissed
      if (!slow || r.endMs > slow._ts) {
        slow = { active: true, kind: 'away', startTs: r.startTs, endTs: r.endTs, observedGal: r.observedGal || null, _ts: r.endMs };
      }
    }
    // Rapid: in progress → always shown; settled → for 24h after it settled.
    // Only an event that crossed one of the viewer's OWN rules; the badge shows
    // THEIR threshold.
    for (const r of rapidRegionsForTank(docs, key, live, tankNum, now)) {
      const inProgress = r.live === true || r.endTs == null;
      if (!inProgress && now - r.endMs > RAPID_BADGE_WINDOW_MS) continue;
      if (!inProgress && r.endMs <= dismRapid) continue; // settled + dismissed
      const mr = matchRapidRule(rapidRules, r);
      if (!mr) continue;
      if (!rapid || r.endMs > rapid._ts) {
        rapid = {
          active: true, kind: 'rapid', state: r.state,
          startTs: r.startTs, endTs: inProgress ? null : r.endTs,
          lastDropTs: r.lastDropTs ?? null, updatedTs: r.updatedTs ?? null,
          gapMs: r.gapMs || 0, endAfterGap: r.endAfterGap === true,
          gallons: mr.gallons, windowMinutes: mr.windowMinutes, observedGal: r.observedGal,
          _ts: r.endMs,
        };
      }
    }
    // Both active on one tank → show whichever fired most recently.
    const chosen = (slow && rapid) ? (rapid._ts >= slow._ts ? rapid : slow) : (rapid || slow);
    // `settled` gates whether the modal offers Dismiss (only a settled event can
    // be dismissed): a nightly leak is always settled; away once its region has
    // closed; rapid only once the cloud has said it settled.
    if (chosen) {
      const { _ts, ...rest } = chosen;
      const settled = chosen.kind === 'slow'
        || (chosen.kind === 'rapid' ? chosen.state === 'settled' || chosen.state === 'ended' : chosen.endTs != null);
      out[tankNum] = { ...rest, tankNum, settled };
    }
  }
  return out;
}

// Chart regions: one discrete band per flagged overnight window (slow), plus
// one band per rapid/away episode (grouped by startT, precise end when closed).
export function buildRegions(docs, alerts, live = null, now = Date.now()) {
  const regions = [];
  for (const key of ['tank1', 'tank2']) {
    const tankNum = key === 'tank1' ? 1 : 2;
    const rapidRules = viewerRapidRules(alerts, tankNum);

    // Slow: highlight ONLY the overnight window that was flagged — one discrete
    // band per suspect night (not merged across nights, never run open-ended to
    // "now"). Prefer the exact fitted sample span (d[key].night.startT/endT);
    // fall back to the fixed 1–5am window. Only if the viewer watches slow leaks.
    if (slowOn(alerts, tankNum)) for (const d of docs) {
      const t = d[key];
      if (!t || !t.severity || t.severity === 'ok') continue;
      const startTs = (t.night && t.night.startT) || Date.parse(d.date + NIGHT_START_UTC);
      const endTs = (t.night && t.night.endT) || Date.parse(d.date + NIGHT_END_UTC);
      regions.push({ tankNum, kind: 'slow', startTs, endTs });
    }

    // Rapid: one band per event. In progress → runs to "now"; while it is
    // confirming, the last drop is marked so the stop shows as it happens.
    // Only events that crossed one of the viewer's OWN rules.
    for (const r of rapidRegionsForTank(docs, key, live, tankNum, now)) {
      if (!matchRapidRule(rapidRules, r)) continue;
      regions.push({
        tankNum, kind: 'rapid', startTs: r.startTs,
        endTs: r.live ? null : r.endTs,
        provisionalEndTs: r.state === 'confirming' ? r.lastDropTs : null,
        observedGal: r.observedGal,
      });
    }

    // Away: one band per episode, same end handling (closed → precise end). Only
    // if the viewer runs Away mode.
    if (awayOn(alerts)) for (const r of episodeRegions(collectHits(docs, key, 'awayHits'), AWAY_ONGOING_MS, now)) {
      regions.push({ tankNum, kind: 'away', startTs: r.startTs, endTs: r.endTs });
    }
  }
  // Collapse the clutter: merge overlapping/adjacent bands of the SAME kind into
  // one, and sort so the higher-priority kind draws on top (rapid > away > slow).
  return sortByPriority(mergeSameKind(regions));
}

// Priority: which kind wins visual attention when bands overlap.
const KIND_PRIORITY = { rapid: 3, away: 2, slow: 1 };

// Merge same-kind, same-tank regions that overlap or nearly touch into one band,
// so a flapping detector doesn't paint a dozen stacked stripes. Ongoing regions
// (endTs null) stay ongoing when merged.
function mergeSameKind(regions) {
  const GAP = 30 * 60000; // treat bands within 30 min as one
  const groups = new Map();
  for (const r of regions) {
    const gk = `${r.tankNum}|${r.kind}`;
    if (!groups.has(gk)) groups.set(gk, []);
    groups.get(gk).push(r);
  }
  const out = [];
  for (const list of groups.values()) {
    list.sort((a, b) => a.startTs - b.startTs);
    let cur = null;
    for (const r of list) {
      const rEnd = r.endTs == null ? Infinity : r.endTs;
      if (!cur) { cur = { ...r, _end: rEnd }; continue; }
      if (r.startTs <= cur._end + GAP) { // overlap / adjacent → merge
        cur._end = Math.max(cur._end, rEnd);
        cur.endTs = (cur.endTs == null || r.endTs == null) ? null : Math.max(cur.endTs, r.endTs);
        cur.observedGal = Math.max(cur.observedGal || 0, r.observedGal || 0) || undefined;
        if (r.provisionalEndTs != null) cur.provisionalEndTs = r.provisionalEndTs;
      } else {
        const { _end, ...rest } = cur; out.push(rest); cur = { ...r, _end: rEnd };
      }
    }
    if (cur) { const { _end, ...rest } = cur; out.push(rest); }
  }
  return out;
}

// Draw lower-priority first so the important band (and its endpoint lines /
// gallons label) sits on top of anything it overlaps.
function sortByPriority(regions) {
  return regions.slice().sort((a, b) => (KIND_PRIORITY[a.kind] || 0) - (KIND_PRIORITY[b.kind] || 0));
}

// History is re-read when an event settles, so the saved entry replaces the
// stand-in quickly. The settled entry is written by a second cloud function a
// moment after the device record changes, hence one delayed re-read as well.
const SETTLE_REREAD_MS = 8000;
function settleKey(live) {
  const ls = live && live.lastSettled;
  if (!ls) return '';
  return [1, 2].map((n) => (ls[n] && ls[n].endT) || 0).join('|');
}

// Fetch leakStats docs with `makeQuery(col)`, re-reading on settle / refresh.
function useLeakDocs(deviceId, enabled, demo, makeQuery, queryKey, refreshKey, live) {
  const [docs, setDocs] = useState([]);
  const sKey = settleKey(live);
  useEffect(() => {
    if (demo || !deviceId || !enabled) { setDocs([]); return undefined; }
    let cancelled = false;
    const read = async () => {
      try {
        const col = collection(db, 'devices', deviceId, 'leakStats');
        const snap = await withRetry(() => getDocs(makeQuery(col)));
        if (!cancelled) setDocs(docList(snap));
      } catch (e) {
        console.error('useLeakData:', e);
        if (!cancelled) setDocs([]);
      }
    };
    read();
    const t = sKey ? setTimeout(read, SETTLE_REREAD_MS) : null;
    return () => { cancelled = true; if (t) clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, enabled, demo, queryKey, refreshKey, sKey]);
  return docs;
}

// Per-tank badge status. `enabled` gates the read so non-leak devices cost 0.
// `alerts` is the VIEWER's own claim prefs — badges are filtered to what they
// personally turned on and the thresholds they set. `live` is the device
// record's leak fields (see top of file); recomputed on every push.
export function useLeakStatus(deviceId, enabled, alerts, live = null) {
  const demo = leakDemoEnabled();
  const docs = useLeakDocs(deviceId, enabled, demo,
    (col) => query(col, orderBy('date', 'desc'), limit(4)), 'status', 0, live);
  const alertsKey = alertsFilterKey(alerts);
  // Re-evaluated every 30 s too, so "dropping" turns into "confirming" on time
  // even between pushes.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(iv);
  }, []);
  return useMemo(() => {
    if (demo) return demoStatus(deviceId);
    if (!deviceId || !enabled) return {};
    return buildStatus(docs, alerts, live);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, deviceId, enabled, docs, alertsKey, live, tick]);
}

// Leak regions since `sinceMs` (the chart's visible start). `enabled` gates it.
// `alerts` is the VIEWER's own claim prefs — bands are filtered to the families
// they turned on and the rapid thresholds they set. `refreshKey` changes force
// a re-read (pull to refresh).
export function useLeakRegions(deviceId, sinceMs, enabled, alerts, live = null, refreshKey = 0) {
  const demo = leakDemoEnabled();
  const sinceDate = sinceMs ? hstDate(sinceMs) : null;
  const docs = useLeakDocs(deviceId, enabled, demo,
    (col) => (sinceDate
      ? query(col, where('date', '>=', sinceDate), orderBy('date', 'asc'), limit(70))
      : query(col, orderBy('date', 'desc'), limit(14))),
    `regions|${sinceDate}`, refreshKey, live);
  const alertsKey = alertsFilterKey(alerts);
  return useMemo(() => {
    if (demo) return demoRegions(deviceId);
    if (!deviceId || !enabled) return [];
    return buildRegions(docs, alerts, live);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, deviceId, enabled, docs, alertsKey, live]);
}
